// RestrictionRules — the capture surface for "who will the owner accept".
//
// Design: design/restriction-rules/ (BRIEF, EXPRESSION_CATALOGUE, VERDICT_A_OR_B).
// DIRECTION B, chosen 2026-08-04: the rule composes as a SENTENCE in place. Its decisive
// property is structural, not visual — "გარდა" is a word in the sentence, so an exception
// CANNOT be authored alone. Direction A had to enforce that with a sheet; B makes it
// impossible to violate. Fail-closed by construction rather than by review.
//
// ⚠️ THE GALLERY IS THE SIBLING, NOT A FORK (core B310, ported here 2026-08-05). The Chrome
// gallery's `rl*` block in `images_viewer.js` learned five things in front of an owner that
// this component did not, and every one of them was a defect here too: the exception offer
// matrix, parent-scoped value menus, same-predicate-replaces, the threshold picker, and
// stripping an axis the agent emptied. They are ported below with their reasoning. The two
// surfaces differ ONLY in where a recorded rule goes — the gallery edits `record.terms.rules`
// and syncs at save time because that page holds no token; this one POSTs immediately.
// A behaviour that diverges beyond that is a bug in one of them, not a local choice.
//
// ⚠️ THREE THINGS THIS FILE MUST NOT DO, each of which would quietly undo the model:
//
//   1. Never hand-compose the preview. Every sentence comes from ruleSentence() so the
//      preview cannot disagree with what the matcher believes. If a case needs a special
//      branch to read well, the MODEL is wrong — say so, do not special-case the copy.
//   2. Never save an exception as a field on its parent. "ორივეს დაფიქსირება" writes TWO
//      rows, each with its own source and certainty, related at read time by subsumption.
//      Folding the relationship into the data would hide it from the certainty layer and
//      stop a later, independently-recorded exception from ever joining the pair.
//   3. Never assert "ვიწრო სძლევს ფართოს" in copy. It is computed by subsumes(), which is
//      parity-tested against the scorer's own predicateSubsumes (core/parity/
//      restriction-subsumption-parity.mjs). Asserting it would promise a relationship the
//      matcher may not honour.
//
// The fast-path chips are MEASURED, not guessed: ცხოველები 979 listings · ეროვნება 931 ·
// მოწევა 49 · სტუდენტები 49 · ბავშვები 32 (design/restriction-rules/CHIP_CORRECTION.md).
// Their shapes differ because the polarity distribution does.

import { Component, useEffect, useMemo, useState } from 'react'
import { Icon } from '../Icon.jsx'
// 🚨 THIS IMPORT IS WHY THE DESK CRASHED (2026-09-08). This file wrote its Georgian inline and
// had never imported `t`; the pet-fee control added on 2026-09-07 called `t('…')` anyway, so
// the moment it rendered the whole tab threw `ReferenceError: t is not defined` and fell into
// TabErrorBoundary — the "ამ ხედის ჩატვირთვა ვერ მოხერხდა" screen agents had been reporting.
// It read as intermittent because the control only draws when pets are ALLOWED, so it was a
// property of the listing, not of the build.
import { t } from '../../lib/i18n.js'
import { pill, PILL_SM, btn, inputCompact, timeAgo } from './primitives.jsx'
import { ruleSentence, subsumes, isExceptionOf, exceptionPolarityOf } from '../../lib/restrictionSentence.js'
import { livePredicateOf, predicateKey, ruleKey } from '../../lib/restrictionRuleKey.js'
// 0927/2026-09-07: the pet FEE is a condition on the pets ANSWER, so it is captured here,
// beside the answer, and nowhere else. See PetFeeBox below for why that is not a layout choice.
import { getListingTerms, amendListingTerms } from '../../db/core.js'
import { nextPetFee, petsPermit } from '../../lib/petFee.js'
import { addRestrictionRule, clearRestrictionAxes, getAxisExpansion, getRestrictionVocab, listRestrictionRules, retractRestrictionRule, setListingPolicy } from '../../db/core.js'

// ⚠️ ნებისმიერი EXISTS ONLY UNDER `allow`, so moving the polarity away from it must drop the
// picks. Leaving them would record a clearance the agent last saw offered under a different
// word — the invisible-mutation shape this file already warns about elsewhere.
const POLARITIES = [
  { v: 'exclude',     ka: 'არ იღებს',    tone: 'warn', icon: 'x' },
  { v: 'accept_only', ka: 'მხოლოდ',      tone: 'warn', icon: 'lock' },
  { v: 'prefer',      ka: 'სასურველია',  tone: 'info', icon: 'check' },
  { v: 'allow',       ka: 'დაიშვება',    tone: 'ok',   icon: 'check' },
]

// ⚠️ THE MEASUREMENT SETS THE DEFAULT ANSWER, NOT THE ONLY ONE (owner, 2026-08-04). Offering
// just the majority answer is faster but strands the minority: an owner who actively wants
// children has no ✓ to press, and hunting for it in the builder is slower than the fast path
// saved. So each subject offers its real poles, the common one first — ★ leads on students
// (30 of 49 preferred), ✗ on children, ✓ on pets (608 allowed vs 404 not).
// Named once: the fee gate below must ask about the SAME set the chip records, or a listing
// answered from the chip would not match the lookup and the box would never appear.
const PETS_VALUES = ['cat', 'dog', 'other']
const FAST = [
  // ⚠️ `exclude_negotiable` is an ANSWER KEY, not a polarity — the polarity is still `exclude`
  // and the row carries negotiable=true (core 0927). It is offered here because "no pets, but
  // ask me" is a stock owner sentence and the alternative was three taps into the builder for
  // the commonest qualified refusal in this market. ✗ and 🤝 are MUTUALLY EXCLUSIVE answers to
  // one question, which is why the lit test below compares the answer KEY and not the polarity.
  { key: 'pets',     ka: 'ცხოველები',  axis: 'pet_species', values: PETS_VALUES, answers: ['allow', 'exclude', 'exclude_negotiable'] },
  { key: 'nat',      ka: 'ეროვნება',   axis: 'nationality', open: true },
  { key: 'smoke',    ka: 'მოწევა',     axis: 'smoking',     values: ['true'], answers: ['allow', 'exclude'] },
  // ✓ is deliberately absent for students: "students are allowed" is the default state of the
  // world and records nothing. ★ and ✗ are the two real answers.
  { key: 'students', ka: 'სტუდენტები', axis: 'profession',  values: ['Student'], answers: ['prefer', 'accept_only', 'exclude'] },
  { key: 'children', ka: 'ბავშვები',   axis: 'age_band',    values: ['child'], answers: ['exclude', 'allow'] },
  // Composition chips (owner, 2026-08-04): "სასურველია წყვილი" is a stock owner sentence.
  // These two have NO corpus count behind them — not because they are rare, but because the
  // flat vocabulary had no couple/family codes to record (composition is 0401's new axis), so
  // the measurement could not see them.
  // ⚠️ ❗ IS OFFERED WHERE "ONLY X" IS A SENTENCE OWNERS ACTUALLY SAY (owner, 2026-08-05: "if
  // owner says only family or only a couple do we have those selections", then "we also need
  // მხოლოდ სტუდენტები"). The builder could always compose them — "იღებს მხოლოდ ოჯახებს,
  // სხვას არავის" is generated correctly — but it was three taps deep for three of the
  // commonest owner statements in this market.
  //
  // It is NOT offered on ცხოველები, მოწევა or ბავშვები, and that is the one judgement left in
  // this list rather than a measurement: an accept_only the household misses is a HARD fail,
  // the strongest thing this panel can write, and "only cats" / "only children" are not
  // statements about who may live in the flat. They stay one level in, where the polarity is
  // named in full next to its three siblings.
  { key: 'couple',   ka: 'წყვილი',     axis: 'composition', values: ['couple'], answers: ['prefer', 'accept_only', 'exclude'] },
  { key: 'family',   ka: 'ოჯახი',      axis: 'composition', values: ['family'], answers: ['prefer', 'accept_only', 'exclude'] },
]

// Exported for scripts/render-negotiable-chip.mjs. The harness must read THIS table, never a
// copy: a second answer list is how ✗ and 🤝 would drift into disagreeing about which one a
// recorded rule lights, which is the exact defect that harness exists to catch.
export const ANSWERS = {
  allow:       { icon: 'check', ka: 'დაიშვება',    tone: 'ok' },
  exclude:     { icon: 'x',     ka: 'არ დაიშვება', tone: 'warn' },
  prefer:      { icon: 'star',  ka: 'სასურველია',  tone: 'info' },
  // Same icon and colour the builder's polarity menu gives it, so one answer does not read as
  // two different things depending on which control recorded it.
  accept_only: { icon: 'lock',  ka: 'მხოლოდ',     tone: 'warn' },
  // 0927. Keeps the REFUSAL tone: it is a refusal, and colouring it like a permission would
  // say the owner agreed to something they did not. The handshake is the whole difference.
  exclude_negotiable: { icon: 'handshake', ka: 'არა, მაგრამ მოლაპარაკებადი', tone: 'warn',
                        polarity: 'exclude', negotiable: true },
}

// Which ANSWER a recorded rule is — the key the chips light on. `exclude` and
// `exclude_negotiable` share a polarity and are different answers, so polarity alone would
// light both chips for one rule and make either tap look like it withdrew the other.
export const answerKeyOf = (rule) =>
  rule?.polarity === 'exclude' && rule?.negotiable === true ? 'exclude_negotiable' : rule?.polarity

// A pet, a cigarette, or the household's SHAPE (couple/family) is a property of the
// HOUSEHOLD; a nationality or an age is a property of a MEMBER. Same expression as the
// gallery's — deliberately not a cleverer derivation, because two spellings of it would put
// two different `applies_to` values on the same recorded fact depending on which surface an
// agent happened to use.
const appliesToOf = (axis) =>
  (axis.startsWith('pet') || axis === 'smoking' || axis === 'composition' || axis === 'conduct') ? 'household' : 'any_member'

// ── WHAT MAY OPEN UNDER WHAT (owner-ruled 2026-08-04, third pass) ─────────────────
// The first pass offered all nine axes and invited "no pets, except Georgians"; the second
// scoped by subject and still offered "no smokers, except Ukrainians", which nobody has said.
//
// The principle that survived: AN EXCEPTION AXIS MUST SLICE THE EXCLUDED SET. Sex, age and
// nationality subdivide "students"; profession does not — it IS the parent, and "no students,
// except an office worker" is not an exception but a non-sequitur, because office workers
// were never excluded. So each axis declares what can refine a rule built on it, and a parent
// offers the union over its axes.
//
// ⚠️ Read this before "simplifying" it: an earlier reading of the owner's note deleted the
// exception feature outright. Exceptions are real on EVERY subject ("no pets, except a small
// cat"); what is not real is refining an ANIMAL rule with a PERSON fact, or the reverse.
// მოწევა and ბავშვები list only their own axis because their real exceptions ("smokes on the
// balcony", "a child over 12") need vocabulary values that arrive with B315 — the button
// appears by itself the day they exist, because axisProductive() reads the data.
const EXC_OFFER = {
  nationality: ['nationality', 'sex', 'age_band', 'profession', 'composition', 'group_size'],
  sex:         ['sex', 'nationality', 'age_band', 'profession', 'composition', 'group_size'],
  profession:  ['profession', 'nationality', 'sex', 'composition', 'group_size'],
  composition: ['composition', 'nationality', 'sex', 'age_band', 'profession', 'group_size'],
  group_size:  ['group_size', 'nationality', 'sex', 'age_band', 'profession', 'composition'],
  age_band:    ['age_band'],
  smoking:     ['smoking'],
  pet_species: ['pet_species', 'pet_size'],
  pet_size:    ['pet_size', 'pet_species'],
}

function excOffered(parent) {
  const parentAxes = Object.keys(livePredicateOf(parent?.predicate))
  const offered = new Set(parentAxes)
  for (const ax of parentAxes) for (const o of (EXC_OFFER[ax] || [])) offered.add(o)
  return offered
}

// Can this axis actually PRODUCE a narrowing against these parent values? Its own axis
// narrows only if several values were named (drop one) or the expansion descends from one
// (foreigner → Indian). A fresh axis always can. This is what keeps the smoker and children
// buttons hidden today and makes them appear BY THEMSELVES the day their vocabulary exists.
function axisProductive(code, parentVals, valuesFor, expansion) {
  if (!valuesFor(code).length) return false
  if (!parentVals) return true
  if (parentVals.length > 1) return true
  const exp = expansion[code] || {}
  return parentVals.some(v => Object.keys(exp[v] || {}).length > 0)
}

function canExcept(rule, valuesFor, expansion) {
  if (rule.polarity !== 'exclude' && rule.polarity !== 'accept_only') return false
  const pred = livePredicateOf(rule.predicate)
  if (!Object.keys(pred).length) return false
  return [...excOffered(rule)].some(code =>
    axisProductive(code, Array.isArray(pred[code]) ? pred[code] : null, valuesFor, expansion))
}

// ⚠️ B316 — AN EXCEPTION TO "ONLY X" IS AN EXCLUSION, NOT A PERMISSION. "მხოლოდ ქართველები,
// გარდა სტუდენტებისა" says some Georgians are NOT accepted; seeding `allow` there produced
// "დაიშვება ქართველი სტუდენტი" — people who were already accepted, a rule that records
// nothing. The matcher needs no pairing for this half: an exclude carve is a restriction in
// its own right and disqualifies on its own (asserted in core/match-score.test.mjs), which is
// why only the composing surface changes.
// ⚠️ MOVED to src/lib/restrictionSentence.js (2026-09-02) so the card face can ask the same
// question. It was private here, which is exactly why CooperationChips printed a parent and its
// exception as two contradicting chips — the panel knew they were one fact and nothing else did.

const idOf = (r) => r.id || ruleKey(r)

// ⚠️ ONLY MY OWN AGENT-RECORDED STANCES ARE TOUCHABLE from this panel — mine, or the
// zero-uuid pre-attribution sentinel that belongs to nobody. A scrape row is the portal's
// checkbox and an ai row is the extractor's reading (neither is any agent's to withdraw, and
// both channels re-write on the next observation); a COLLEAGUE's attributed stance is their
// observation exactly like their rule would be. The distinction is not politeness: the
// /policy clear deletes precisely the caller's rows plus the sentinel, so offering 🗑 on
// anything else would no-op silently and the "removed" chip reappears on reload — the exact
// lie this panel's read-only badge exists to prevent. Read-only rows still lose the argument
// to a rule on the same predicate via the scorer's claim mechanism, untouched.
const ZERO_AGENT = '00000000-0000-0000-0000-000000000000'
const legacyEditable = (r, agentId) => r.origin === 'legacy_stance'
  && r.source_kind === 'agent' && !!r.legacy_subject
  && (r.recorded_by_agent_id === ZERO_AGENT || (!!agentId && r.recorded_by_agent_id === agentId))

// ⚠️ THE SAME QUESTION FOR A RULE, WHICH NOBODY WAS ASKING (2026-08-07). Everything above
// applies word for word to a rule, and the panel asked it only of stances: a colleague's
// RULE drew a live 🗑 and a lit, tappable fast-chip, identical to one you recorded yourself.
// So a real agent opened a colleague's listing, read three lit chips as his own empty form,
// and withdrew one of her restrictions (listing c06dd496 — the `prefer nationality` rule).
// The server's own-rules-only guard should have refused him and could not, because every
// Desk-written rule carried the sentinel; both halves are fixed (migration 0475, and the
// agentId the transport was dropping), and this is the half that stops the MISREADING
// rather than the write — the guard turns his tap into an error, this turns it into a
// question he never asks.
//
// The sentinel stays editable on purpose, matching the server and the stance rule above:
// after 0475 it means the 0413 stance migration or an unresolvable account, i.e. a row that
// really is nobody's, and hiding 🗑 there would strand the upgrade-on-touch conversion.
// ⚠️ NEVER `e.message` INTO THE UI. The whole panel is Georgian and `coreApi` throws with the
// server's English `error` string, so `setErr(errKa(e, 'ვერ წაიშალა'))` rendered
// "no live rule of yours with that id on this listing" to an agent — and the Georgian fallback
// beside it could never fire, because `message` is always truthy. That shape reads like a
// deliberate fallback and is dead code.
//
// Branch on `code` (stable, server-declared) and fall back to the caller's own Georgian
// sentence. An unrecognised failure says "it did not save", which is true and useful; the
// English detail belongs in the console, where it already goes.
const ERR_KA = {
  not_yours: 'ეს ჩანაწერი სხვა აგენტისაა — წაშლა შეუძლებელია.',
}
const errKa = (e, fallback) => {
  if (e?.code && ERR_KA[e.code]) return ERR_KA[e.code]
  if (e) console.warn('[restrictions]', e.status || '', e.message)
  return fallback
}

const ruleEditable = (r, agentId) => r.origin === 'legacy_stance'
  ? legacyEditable(r, agentId)
  : r.recorded_by_agent_id === ZERO_AGENT || (!!agentId && r.recorded_by_agent_id === agentId)

// ── ONE FACT, ONE ROW, AND THE NAMES ON IT (owner, 2026-08-08) ────────────────────
// Reported from an admin card showing EIGHT records where two were recorded: "repeted
// statemnts? is it because many agents selected the same?" — and the measurement said both
// readings exist in this corpus and they are opposite facts:
//   · 55 surplus rows were ONE agent's save loop firing repeatedly. A defect; core 0493 now
//     makes the database refuse the second copy, so they stop arriving.
//   · 74 surplus rows across 45 listings are DIFFERENT agents recording the same restriction,
//     which is corroboration and is precisely what evidence-certainty reads to call a fact
//     verified. 0399 has no unique constraint for exactly this reason.
// Rendered as identical repeated sentences those two are indistinguishable, so the screen
// turned agreement into what looks like a bug. Grouping is not tidying: it is what lets the
// card say WHICH of the two it is holding.
//
// ⚠️ THE LABEL ADDED THE DAY BEFORE THIS IS WHAT MADE IT ANSWERABLE AND ALSO WHAT MADE IT
// GLARING. `ruleEditable` above put "სხვა აგენტის ჩანაწერი" on rules too — the right fix for
// irakli withdrawing Gvantsa's restriction — but it names a CATEGORY, and eight rows each
// saying "another agent's record" reads as eight colleagues. The byline below finishes that
// thought by naming the person.
//
// Keyed by ruleKey — the same canonical (polarity + predicate) identity the writers dedupe on
// and 0493's index enforces — so the card cannot group a set the matcher reads as two
// different rules, or split one it reads as one.
function groupRules(rules) {
  const out = []
  const byKey = new Map()
  for (const r of rules) {
    const k = ruleKey(r)
    const g = byKey.get(k)
    if (g) { g.members.push(r); continue }
    // The FIRST member leads: it is the act, and everything after it is agreement with it.
    // The server orders by created_at, so this is oldest-first without re-sorting here.
    const fresh = { key: k, rep: r, members: [r] }
    byKey.set(k, fresh); out.push(fresh)
  }
  return out
}

// Who stated it. A name when we have one, and otherwise the honest category — never a
// placeholder, because "Unknown" reads as a person whose name we lost rather than a channel
// that has no person behind it. `recorded_by_name` is served by core_listing_terms.js, which
// resolves it per listing AFTER the earned gate has decided what this viewer may see.
const RECORDER_FALLBACK = { scrape: 'წყაროდან', ai: 'AI-მ ამოიკითხა', admin: 'მენეჯმენტი' }
// 🔴 A NAMED AGENT DID THIS WORK, AND THE PANEL CALLED IT "the AI" (owner ruling 2026-09-10:
// "we may name that restrictions author idmon agent — idmon is an agent on its way, and make
// sure that everyone can see idmon's details"). Idmon reads every advert's prose, and 433 of
// the live rules are its work. Under the old label they were indistinguishable from the other
// two AI writers, so nobody could tell whose reading they were trusting or disputing.
//
// ⚠️ `source_agent` is NULL for an unnamed writer and that MUST keep falling through to the
// generic label — 299 AI rules come from `ai_patch_rules@v1` and `fb_lead_rules@v1`, which
// nobody has named. Printing "Idmon" over those would be the misattribution the column was
// added to prevent (migration 1029).
const AGENT_LABEL = { idmon: 'იდმონი', lynceus: 'ლინკევსი' }
// ONE definition, used by both groupings below. They carried the same expression written twice
// and were one edit away from disagreeing about who wrote a rule — on a panel whose entire job
// is answering "who recorded what".
const recorderLabelOf = (r, mine = false) => mine ? 'თქვენ'
  : (r.recorded_by_name || AGENT_LABEL[r.source_agent] || RECORDER_FALLBACK[r.source_kind] || 'სხვა აგენტი')
function recordersOf(members, agentId) {
  const seen = new Set()
  const out = []
  for (const m of members) {
    const mine = ruleEditable(m, agentId)
    const label = recorderLabelOf(m, mine)
    if (seen.has(label)) continue
    seen.add(label); out.push({ label, mine })
  }
  // Mine first: on a card that may hold four names, the agent's own standing on this
  // restriction is the thing they came to check.
  return out.sort((a, b) => (b.mine ? 1 : 0) - (a.mine ? 1 : 0))
}

// The property modal answers a different question from the editor: not merely "what is the
// working restriction?", but "who recorded what, and when?". Grouping by ruleKey alone used
// to collapse two agents who independently recorded the same condition into one anonymous
// chip. Keep one freshest copy per recorder + canonical rule, then stack that recorder's
// statements beneath their name. Source channels get honest labels rather than invented
// people; attributed agents are keyed by their ids so two equal display names do not merge.
function rulesByRecorder(rules) {
  const groups = new Map()
  for (const r of rules || []) {
    // The KEY carries source_agent too, or Idmon and an unnamed AI writer collapse into one
    // "AI" recorder group and their statements stack under a single name that is true of
    // only some of them.
    const recorderKey = r.recorded_by_party_id || r.recorded_by_agent_id
      || `${r.source_kind || 'agent'}:${r.source_agent || ''}:${r.recorded_by_name || ''}`
    const label = recorderLabelOf(r)
    if (!groups.has(recorderKey)) groups.set(recorderKey, { key: recorderKey, label, rules: new Map(), latest: null })
    const group = groups.get(recorderKey)
    const at = r.last_seen_at || r.created_at || null
    const key = ruleKey(r)
    const previous = group.rules.get(key)
    const previousAt = previous && (previous.last_seen_at || previous.created_at || '')
    if (!previous || String(at || '') > String(previousAt)) group.rules.set(key, r)
    if (at && (!group.latest || at > group.latest)) group.latest = at
  }
  return [...groups.values()].sort((a, b) => String(b.latest || '').localeCompare(String(a.latest || '')))
}

// ⚠️ "NOT RESTRICTED" IS A CLAIM ABOUT THE LISTING, AND ONE RULE CANNOT MAKE IT ALONE.
// An `accept_only` names the axes it does NOT restrict, because "only girls" and "only
// Georgian girls" are different rules and the flat `only` stance could not tell them apart.
// That argument holds inside one rule and stops at its edge: with "მხოლოდ წყვილი", "არ იღებს
// ცხოველებს" and "არ იღებს ინდოელებს" all recorded — one owner sentence, three rules, because
// they cannot share a polarity — the first card announced ეროვნება as unrestricted directly
// above a card excluding Indians. Every word of it true of that rule, and false of the flat.
// So an axis another live rule speaks about is withheld here (`hideAxes`, the hook the
// generator already carries) rather than contradicted on screen.
const axesSpokenBy = (rules) =>
  rules.flatMap(r => Object.keys(livePredicateOf(r.predicate)))

// Compact, read-only projection for the property modal. It deliberately owns its fetches
// instead of extending RentalTermsByAgent's older terms/policies batch: restriction_rule is
// a separate read model, and mixing the two made a failure in this optional summary take down
// the whole property view. The boundary is local for the same reason — a malformed historic
// rule may hide this one line, but must never make the property impossible to open.
class RestrictionRulesSummaryBoundary extends Component {
  state = { failed: false }
  static getDerivedStateFromError() { return { failed: true } }
  render() { return this.state.failed ? null : this.props.children }
}

function RestrictionRulesSummaryBody({ listingIds, onOpen }) {
  const ids = useMemo(() => [...new Set((listingIds || []).filter(Boolean))], [listingIds?.join(',')])
  const [state, setState] = useState(null)
  useEffect(() => {
    let gone = false
    if (!ids.length) { setState({ vocab: null, rules: [] }); return () => { gone = true } }
    Promise.all([
      getRestrictionVocab().catch(() => null),
      Promise.all(ids.map(id => listRestrictionRules(id).catch(() => ({ rules: [] })))),
    ]).then(([vocab, responses]) => {
      if (gone) return
      setState({ vocab, rules: responses.flatMap((r, i) => (r?.rules || [])
        .filter(rule => rule.origin !== 'legacy_stance')
        .map(rule => ({ ...rule, _listingId: ids[i] }))) })
    })
    return () => { gone = true }
  }, [ids.join(',')])
  if (!state) return <span className="text-xs text-muted-foreground">…</span>
  const grouped = rulesByRecorder(state.rules)
  if (!grouped.length) return null
  return (
    <div className="space-y-2">
      {grouped.map(g => (
        <div key={g.key} className="space-y-1">
          <div className="flex items-baseline gap-2 text-xs">
            <span className="font-medium text-foreground">{g.label}</span>
            {g.latest && <span className="text-muted-foreground">{timeAgo(g.latest)}</span>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[...g.rules.values()].map(rule => {
              const text = sentenceOf(rule, state.vocab)
              const target = rule._listingId || ids[0]
              return onOpen && target
                ? <button key={ruleKey(rule)} type="button" onClick={() => onOpen(target)}
                    className={pill('none', { door: true, extra: 'max-w-full whitespace-normal text-left' })}>{text}</button>
                : <span key={ruleKey(rule)}
                    className={pill('none', { extra: 'max-w-full whitespace-normal text-left' })}>{text}</span>
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export function RestrictionRulesSummary(props) {
  return <RestrictionRulesSummaryBoundary><RestrictionRulesSummaryBody {...props} /></RestrictionRulesSummaryBoundary>
}

export default function RestrictionRules({ listingId, saleMode = false, agentId = null, onChanged }) {
  const [vocab, setVocab] = useState(null)
  const [expansion, setExpansion] = useState({})
  const [rules, setRules] = useState([])
  // The server's "hidden ≠ absent" stubs (projectEarned): rules withheld from THIS viewer
  // until they record their own. Dropped on the floor until 2026-08-06, which made the
  // panel say "ჯერ არაფერია ჩაწერილი" over a listing that held recorded rules — a lie,
  // not a withholding, and the exact confusion the stub shape exists to prevent.
  const [hidden, setHidden] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [note, setNote] = useState(null)
  const [busy, setBusy] = useState(false)
  // The rule being composed: null = closed. `parent` set = composing an EXCEPTION to it,
  // which is the only way an exception can be created (see the header).
  const [draft, setDraft] = useState(null)
  // Axes the agent asked to see in full, for this draft only.
  const [expanded, setExpanded] = useState(() => new Set())

  useEffect(() => {
    let gone = false
    setLoading(true); setErr(null)
    Promise.all([
      getRestrictionVocab().catch(() => null),
      getAxisExpansion().catch(() => ({})),
      listRestrictionRules(listingId).catch(() => ({ rules: [] })),
    ]).then(([v, x, r]) => {
      if (gone) return
      setVocab(v); setExpansion(x || {}); setRules(r?.rules || []); setHidden(r?.hidden || null)
      setLoading(false)
    }).catch(e => { if (!gone) { setErr(errKa(e, 'პირობები ვერ ჩაიტვირთა')); setLoading(false) } })
    return () => { gone = true }
  }, [listingId])

  // SALE MODE is one axis, not a second design (0400 moved payment to listing_terms, so
  // nothing else survives): a seller states no pet, smoker, student or composition
  // conditions — those are tenancy concepts.
  const allAxes = useMemo(() => {
    const all = (vocab?.axes || []).filter(a => a.code !== 'feature')
    return saleMode ? all.filter(a => a.code === 'nationality') : all
  }, [vocab, saleMode])

  const valuesFor = (axis) => (vocab?.values || []).filter(v => v.axis_code === axis)

  // The axes offered for the draft on screen: everything in scope, narrowed to what may
  // refine the parent when one is being excepted.
  const axesFor = (d) => {
    const parent = d?.parent
    if (!parent) return allAxes
    const offered = excOffered(parent)
    return allAxes.filter(a => offered.has(a.code))
  }

  const closeDraft = () => { setDraft(null); setExpanded(new Set()) }

  async function reload() {
    const r = await listRestrictionRules(listingId).catch(() => ({ rules: [] }))
    setRules(r?.rules || []); setHidden(r?.hidden || null); onChanged?.()
    window.dispatchEvent(new CustomEvent('sentavus:restriction-rules-changed', { detail: { listingId } }))
  }

  // A rule already recorded by ME that answers this predicate. Legacy stances are handled
  // separately below — a stance is not a rule and has no rule row to retract.
  //
  // ⚠️ The name was true and the filter was not (fixed 2026-08-07): it matched the predicate
  // and never the recorder, so replacing an answer fired a retract at every colleague's rule
  // on the same question and relied on the server to say no. It said yes — every Desk rule
  // sat on the zero sentinel — so "answering a question you have already answered" silently
  // withdrew answers this agent never gave. The guard is fixed (0475); asking only for rows
  // that are actually mine is the half that does not depend on it.
  const mineAnswering = (pk) => rules.filter(r =>
    r.origin !== 'legacy_stance' && ruleEditable(r, agentId) && predicateKey(r) === pk)
  const theirsAnswering = (pk) => rules.filter(r =>
    r.origin !== 'legacy_stance' && !ruleEditable(r, agentId) && predicateKey(r) === pk)

  // ── UPGRADE-ON-TOUCH (owner, 2026-08-05: "convert each one as it would be in the new
  // system"). The CORPUS is not converted — restriction_rule_v already projects every
  // stance into rule shape at read time, and materializing that projection would let the
  // corroboration machinery read one observation as two agreeing sources (a lone stance
  // inflated toward `verified`), while the stances stay a LIVE capture channel that crawls
  // re-write. What converts is each answer THE MOMENT AN AGENT TOUCHES IT: withdrawing or
  // re-answering a legacy row in this panel clears the stance and records the correction as
  // a rule — provenance intact, one act at a time, and the grid's volume drains toward the
  // B307 retirement condition instead of waiting for it.
  //
  const legacyAnswering = (pk) => rules.filter(r => legacyEditable(r, agentId) && predicateKey(r) === pk)

  // ⚠️ ONE RECORDING PATH, and everything that keys on a predicate happens inside it.
  //
  //   1. STRIP AXES THE AGENT CLEARED. The exception builder keeps its parent's axes visible
  //      after they are emptied, so a draft legitimately carries `{pet_species: [],
  //      pet_size: ['small']}` — and an empty axis is NOT an axis: it gives one rule two
  //      keys, reaches the 0399 write guard as a claim about nothing, and made subsumes()
  //      bless a rule that had stopped naming the set it refines ([].every is vacuously
  //      true). This component used to strip only when there was no parent, i.e. never in
  //      the case that produces them.
  //   2. A DOUBLE TAP IS NOT A SECOND OBSERVATION. The database deliberately allows two
  //      AGENTS to state one restriction twice (that is corroboration, and why 0399 has no
  //      unique constraint). One agent stating it twice on one screen is a slip.
  //   3. THE OPPOSITE ANSWER MUST NOT SURVIVE ALONGSIDE THE NEW ONE. A predicate is a
  //      question and a polarity is its answer: recording ✓ and then ✗ for pets used to keep
  //      BOTH live, and the matcher reads a disqualifying stance as authoritative — so the
  //      contradiction silently dropped every client with a pet while the screen also said
  //      pets were fine. Answering a question you have already answered REPLACES the answer.
  //      Only your OWN answer: a colleague's opposite reading is a real disagreement for the
  //      certainty layer to weigh, so it is reported, never retracted (the server refuses it
  //      anyway — retract is own-rules-only by plan A).
  async function record(rule) {
    const predicate = livePredicateOf(rule.predicate)
    // review 2026-09-23: ნებისმიერი alone is a complete answer (an axis clearance, 0739/0747 —
    // restrictions-clear accepts `axes` on its own); only a draft with neither is empty.
    const hasAny = Array.isArray(rule.anyAxes) && rule.anyAxes.filter(Boolean).length > 0
    const hasPred = Object.keys(predicate).length > 0
    if (!hasPred && !hasAny) { closeDraft(); return }
    const shaped = { ...rule, predicate }
    // review 2026-09-23: the double-tap guard compares against MY live rules only. Matching a
    // colleague's identical rule made it impossible to switch to an answer they had already given
    // (and my own opposite answer was never retracted). Their copy is corroboration (0399 allows it).
    if (!hasAny && rules.some(r => r.origin !== 'legacy_stance' && ruleEditable(r, agentId)
        && ruleKey(r) === ruleKey(shaped))) { closeDraft(); return }

    setBusy(true); setErr(null); setNote(null)
    try {
      // A colleague's opposite reading is a real disagreement for the certainty layer to
      // weigh — reported, never retracted, and now never even ASKED for.
      // A clearance-only answer names no predicate, so there is no earlier answer to replace.
      let refused = hasPred ? theirsAnswering(predicateKey(shaped)).length : 0
      for (const other of hasPred ? mineAnswering(predicateKey(shaped)) : []) {
        // Serial on purpose: the old answer must be gone before the new one lands, or a
        // reader between the two calls sees a contradiction the agent never stated.
        const gone = await retractRestrictionRule(listingId, other.id, agentId).then(() => true).catch(() => false)
        if (!gone) refused += 1
      }
      // Upgrade-on-touch: an agent-recorded flat stance answering this same predicate is
      // the OLD spelling of the answer being given now — clear it, or the screen shows the
      // fresh rule beside its own superseded chip as a phantom second opinion. The server
      // clears only the caller's own row plus the unattributed sentinel, so a colleague's
      // attributed stance survives exactly like their rule would.
      for (const legacy of hasPred ? legacyAnswering(predicateKey(shaped)) : []) {
        await setListingPolicy(listingId, legacy.legacy_subject, null, undefined, agentId).catch(() => {})
      }
      // ── A CLEARANCE IS NOT A RULE, AND IT GOES SOMEWHERE ELSE ────────────────────────
      // ნებისმიერი on an axis says "no restriction on this axis", which restriction_rule
      // cannot express: assert_restriction_rule refuses an empty value array, and every axis
      // is a value enum with no "any" member. Its home is core 0739's per-axis clearance.
      // ⚠️ The two can be recorded TOGETHER — "any nationality, students preferred" is one
      // answer touching two axes — so this is not an either/or branch. The clearance lands
      // FIRST: if the rule write then fails the agent sees an error over a half-saved answer,
      // whereas the reverse leaves a recorded rule beside a clearance that never landed, and
      // a clearance that silently did not happen reads as "no restriction" to nobody.
      const anyList = Array.isArray(shaped.anyAxes) ? shaped.anyAxes.filter(Boolean) : []
      if (anyList.length) {
        await clearRestrictionAxes(listingId, anyList,
          (shaped.verbatim || '').trim() || undefined, agentId)
      }
      const hasPredicate = Object.keys(shaped.predicate || {}).length > 0
      if (hasPredicate) await addRestrictionRule(listingId, {
        polarity: shaped.polarity, predicate: shaped.predicate,
        appliesTo: shaped.appliesTo, verbatim: (shaped.verbatim || '').trim() || undefined,
        negotiable: shaped.negotiable === true,
      }, agentId)
      if (refused) setNote('სხვა აგენტს საპირისპირო პასუხი აქვს ჩაწერილი. ორივე პასუხი ჩანს.')
      await reload()
      closeDraft()
    } catch (e) { setErr(errKa(e, 'შენახვა ვერ მოხერხდა')) } finally { setBusy(false) }
  }

  // The exception pair. The button only opens from an already-recorded rule, so the parent
  // is on the server already — but a parent that is somehow unsaved must land FIRST: a
  // half-saved pair whose exception exists without its restriction reads as a permission the
  // owner never gave.
  async function savePair(parent, exception) {
    if (!parent.id) {
      setBusy(true)
      try { await addRestrictionRule(listingId, parent, agentId) }
      catch (e) { setErr(errKa(e, 'შენახვა ვერ მოხერხდა')); setBusy(false); return }
      setBusy(false)
    }
    await record(exception)
  }

  // ⚠️ A LIST, because the card is grouped now and one 🗑 may stand over several rows I
  // recorded. It withdraws MINE and only mine — a colleague's agreeing observation in the same
  // group is their statement, the server refuses it anyway (own-rules-only, plan A), and after
  // 0475 that refusal is a real 403 rather than the silent no-op it used to be.
  //
  // The guard asks `ruleEditable`, not `legacyEditable`, so it covers RULES too, and it is
  // applied HERE rather than trusted from the caller: the guarantee should hold in this
  // function, not rest on two components continuing to agree.
  //
  // Serial on purpose: one reload at the end, and a failure stops rather than half-clearing.
  async function retractAll(list) {
    const doable = (list || []).filter(r => ruleEditable(r, agentId))
    if (!doable.length) return
    setBusy(true); setErr(null); setNote(null)
    try {
      for (const rule of doable) {
        // A legacy stance withdraws through ITS OWN write path (the stance clear), not the
        // rule retract — it has no rule row.
        if (rule.origin === 'legacy_stance') await setListingPolicy(listingId, rule.legacy_subject, null, undefined, agentId)
        else await retractRestrictionRule(listingId, rule.id, agentId)
      }
      await reload()
    }
    catch (e) { setErr(errKa(e, 'ვერ წაიშალა')) } finally { setBusy(false) }
  }
  const retract = (rule) => retractAll([rule])

  // Which rules are ALREADY drawn as a link in someone else's chain. An exception used to be
  // drawn twice — nested under its parent AND again as its own card — so one recorded fact
  // looked like two restrictions, and the pair the whole model exists to express read as a
  // contradiction sitting next to itself (owner, 2026-08-04: "no more cards, only chain
  // below"). Computed by the same predicate the chain uses, so the two cannot disagree.
  const nested = useMemo(() => {
    const out = new Set()
    for (const parent of rules) for (const other of rules)
      if (isExceptionOf(parent, other, expansion)) out.add(idOf(other))
    return out
  }, [rules, expansion])

  // One row per distinct recorded condition. Nesting is decided on the REPRESENTATIVE and that
  // is exact, not an approximation: isExceptionOf reads only polarity and predicate, and a
  // group is by construction one polarity over one canonical predicate — so every member of a
  // group nests or none of them does.
  const groups = useMemo(() => groupRules(rules), [rules])

  if (loading) return <p className="text-xs text-muted-foreground">…</p>

  return (
    <div className="space-y-3">
      {err && <p className="text-xs text-destructive">{err}</p>}
      {note && <p className="text-xs text-amber-600 dark:text-amber-400">{note}</p>}

      {/* FAST PATH — one tap for the volume cases. Not shown in sale mode, where the only
          axis is nationality and the builder is the whole surface. */}
      {!saleMode && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">სწრაფი პასუხი</p>
          {/* `current`: an EDITABLE legacy stance lights the chip exactly like a rule —
              its answer is the answer, whichever system recorded it. Tapping the lit answer
              withdraws through the stance's own path; tapping another REPLACES it with a
              rule (upgrade-on-touch). Scrape/ai stances never light: a lit answer promises
              withdrawal, and those are not any agent's to withdraw.

              `theirs` is that same sentence finally applied to RULES (2026-08-07). A
              colleague's rule must still light — it is the truth about the flat, and a chip
              that showed nothing would invite this agent to record a contradicting answer —
              but it lights as a STATEMENT, not a control. Only `current` drives the withdraw
              tap, and it now names only rows this agent can actually withdraw. Before this
              split the two were one prop, so her answer and his own were the same pixel. */}
          <div className="flex flex-wrap gap-1.5">
            {FAST.map(f => {
              const answers = f.open ? [] : rules.filter(r =>
                predicateKey(r) === predicateKey({ predicate: { [f.axis]: f.values } }))
              return (
              <FastGroup key={f.key} spec={f} busy={busy}
                current={answers.find(r => ruleEditable(r, agentId)) || null}
                theirs={answers.find(r => !ruleEditable(r, agentId)) || null}
                onOpen={() => { setExpanded(new Set([f.axis])); setDraft({ polarity: 'exclude', predicate: { [f.axis]: [] }, verbatim: '' }) }}
                // The chip speaks ANSWER keys; the gateway speaks polarity + negotiable.
                // Translated here, once, so no other caller has to know the pseudo-key exists.
                onPick={(answer) => record({ polarity: ANSWERS[answer]?.polarity || answer,
                                             negotiable: ANSWERS[answer]?.negotiable === true,
                                             predicate: { [f.axis]: f.values },
                                             appliesTo: appliesToOf(f.axis) })}
                onWithdraw={(rule) => retract(rule)}
                // Deliberately states the FACT and stops. It does not suggest recording the
                // opposite: that is a legitimate second opinion, but until the search stops
                // reading deny+allow as `conflicting` it can shelve the flat — so nudging an
                // agent into it from a blocked tap would be routing them into a known defect.
                onBlocked={() => { setErr(null); setNote('ეს პასუხი სხვა აგენტმა ჩაწერა — მისი წაშლა თქვენ ვერ შეძლებთ.') }} />
              )
            })}
          </div>
          {/* The fee appears only under a PERMISSION. Under ✗ or 🤝 there is nothing to price:
              a refusal with an amount beside it is the contradiction this move exists to end.
              An unanswered pets question shows nothing either — a price box on a question
              nobody has answered invites recording the fee for a flat that may refuse pets. */}
          {(() => {
            // The decision lives in lib/petFee.js — see its header for why it is not inline.
            const permits = petsPermit(rules, predicateKey,
              predicateKey({ predicate: { pet_species: PETS_VALUES } }))
            return permits
              ? <div className="mt-1.5"><PetFeeBox listingId={listingId} agentId={agentId} disabled={busy} /></div>
              : null
          })()}
        </div>
      )}

      {/* RECORDED RULES */}
      <div>
        <p className="text-xs text-muted-foreground mb-1">
          {/* ⚠️ CONDITIONS, NOT ROWS. This counted rows until 2026-08-08 and announced "· 8"
              over two recorded conditions — the number that made the owner ask whether eight
              agents had answered. A count beside a list must count the things in the list. */}
          ჩაწერილი პირობები {groups.length > 0 && <span className="opacity-60">· {groups.length}</span>}
        </p>
        {rules.length === 0 && !(hidden?.policies?.length) && (
          <p className="text-xs text-muted-foreground/70">ჯერ არაფერია ჩაწერილი.</p>
        )}
        {/* Hidden ≠ absent (projectEarned): withheld rules come back as counted stubs so
            this panel can say the truth — recorded, not yours to see yet — instead of
            "nothing recorded", which reads as an invitation to record it all again. */}
        {(hidden?.policies?.length || 0) > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            <Icon name="lock" /> {hidden.policies.reduce((s, h) => s + (h.count || 1), 0)} ჩანაწერი დამალულია — ჩაწერე შენი, რომ ნახო
          </p>
        )}
        <div className="space-y-1.5">
          {groups.filter(g => !nested.has(idOf(g.rep))).map(g => { const r = g.rep; return (
            <RuleRow key={g.key} rule={r} group={g} groups={groups} vocab={vocab} rules={rules}
              expansion={expansion} busy={busy} agentId={agentId}
              canExcept={canExcept(r, valuesFor, expansion)}
              onRetract={target => retractAll((target || g).members)}
              onExcept={() => {
                // The draft STARTS AS the parent's own set, copied. It used to start emptied
                // ("copying would make it identical, which subsumes refuses") — but that
                // forced the agent to re-click სტუდენტი on every students exception before
                // the pair would relate, and forgetting it silently produced a free-standing
                // rule. Identical-to-parent is already handled: subsumes() reports "not an
                // exception" until something actually narrows, and record() de-duplicates.
                const seed = {}
                for (const [k, v] of Object.entries(livePredicateOf(r.predicate)))
                  seed[k] = Array.isArray(v) ? [...v] : v
                setExpanded(new Set())
                setDraft({ polarity: exceptionPolarityOf(r), predicate: seed, verbatim: '', parent: r })
              }} />
          ) })}
        </div>
      </div>

      {/* BUILDER */}
      {draft
        ? <Builder draft={draft} setDraft={setDraft} axes={axesFor(draft)} valuesFor={valuesFor} vocab={vocab}
            expansion={expansion} rules={rules} busy={busy}
            expandedAxes={expanded} onExpandAxis={(code) => setExpanded(s => new Set(s).add(code))}
            onCancel={closeDraft}
            onSave={() => draft.parent
              ? savePair(draft.parent, { polarity: draft.polarity, predicate: draft.predicate,
                                         appliesTo: draft.appliesTo, verbatim: draft.verbatim })
              : record({ polarity: draft.polarity, predicate: draft.predicate,
                         appliesTo: draft.appliesTo, verbatim: draft.verbatim,
                         negotiable: draft.negotiable === true,
                         anyAxes: draft.anyAxes })} />
        : <button type="button" className={pill('neutral', { door: true, extra: 'border-dashed' })}
            onClick={() => setDraft({ polarity: 'exclude', predicate: {}, verbatim: '' })}>
            <Icon name="plus" /> სხვა პირობა
          </button>}
    </div>
  )
}

// ⚠️ ONE BORDERED GROUP PER SUBJECT — its name, then the answers it accepts. The first
// version drew each answer as its own coloured pill ("✓ ცხოველები" green beside a bare "✗"
// red) and three things were wrong at once, all the same mistake: THE COLOUR SHOWED THE
// OPTION, NOT THE ANSWER. An unanswered subject already looked answered, in two colours; the
// bare ✗ carried no subject, so it read as a control of its own; and nothing showed what had
// been recorded, so the only way to see the answer was to read the list below. Now the group
// is neutral until answered, the chosen answer is lit, and tapping the lit answer withdraws
// it — the same retraction the 🗑 offers, where the agent is already looking.
// ── THE PET FEE HANGS OFF THE PETS ANSWER (owner ruling 2026-09-07) ─────────────────────────
// "what we do is: are pets allowed by a deposit or not at all?" — ONE question with three
// answers, not two independent facts. The deposit is a CONDITION on the permission: "costs
// extra" can only exist inside "allowed".
//
// 🔴 IT LIVED IN THE TERMS PANEL AND THAT PRODUCED CONTRADICTIONS ON REAL ROWS. Measured
// 2026-09-07: 23 listings said a pet costs extra, and SEVEN of them also carried a rule saying
// no pets at all — a fee on a flat that refuses animals. Nearly a third. Not one of the seven
// named an amount, so the fee asserted nothing while contradicting the permission beside it.
// Two panels asking one question is how that happens, and moving the box here is what makes it
// structurally impossible rather than something a query finds afterwards.
//
// ⚠️ ONE EDITOR. The box was removed from ManualPropertyModal's terms section in the same
// change. Leaving it in both would give one fact two editors — the exact thing that comment in
// the modal has warned about since pets/smoking/children left that list in July.
function PetFeeBox({ listingId, agentId, disabled }) {
  const [terms, setTerms] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    if (!listingId) return
    let gone = false
    getListingTerms(listingId).then(r => { if (!gone) setTerms(r || {}) }).catch(() => { if (!gone) setTerms({}) })
    return () => { gone = true }
  }, [listingId])
  if (!terms) return null

  const amount = terms.pet_deposit_extra ?? ''
  const required = terms.pet_deposit_required
  // Writes on BLUR, never per keystroke: amend_listing_terms is evented, and a version per
  // digit would fill the amendment log with a number nobody typed on purpose.
  const commit = async (v) => {
    const next = nextPetFee({ pet_deposit_extra: amount, pet_deposit_required: required }, v)
    const before = { extra: amount === '' ? null : Number(amount), req: required }
    const after = { extra: next.pet_deposit_extra === '' ? null : Number(next.pet_deposit_extra),
                    req: next.pet_deposit_required === '' ? null : next.pet_deposit_required === 'true' }
    if (before.extra === after.extra && before.req === after.req) return
    const ops = []
    ops.push(after.extra == null ? { op: 'clear', field: 'pet_deposit_extra' }
                                 : { op: 'set', field: 'pet_deposit_extra', value: after.extra })
    // A null answer is written as a CLEAR, never as false — "nobody asked" is not "no".
    ops.push(after.req == null ? { op: 'clear', field: 'pet_deposit_required' }
                               : { op: 'set', field: 'pet_deposit_required', value: after.req })
    setTerms(x => ({ ...x, pet_deposit_extra: after.extra, pet_deposit_required: next.pet_deposit_required }))
    try { await amendListingTerms(listingId, ops, agentId) } catch (e) { setErr(e.message || 'შენახვა ვერ მოხერხდა') }
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-pet-fee>
      <span>{t('desk_terms_extra_pet_deposit')}</span>
      <input type="number" min="0" disabled={disabled} defaultValue={amount}
        onBlur={e => commit(e.target.value)}
        className="rounded-lg border border-input bg-background px-2 py-1 text-xs w-20" />
      <span className="opacity-70">{terms.deposit_currency || ''}</span>
      {/* 21 of the 23 rows that say a pet costs extra hold no amount (the flag and the field
          were captured years apart). Their empty box would read "free" — the screen
          contradicting the record — so it says so until somebody types the number. */}
      {(required === true) && (amount === '' || amount == null) && (
        <span className="text-amber-600 dark:text-amber-400" data-pet-fee-unknown>
          {t('desk_terms_pet_fee_unknown')}
        </span>
      )}
      {err && <span className="text-destructive">{err}</span>}
    </span>
  )
}

function FastGroup({ spec, current, theirs, busy, onPick, onWithdraw, onOpen, onBlocked }) {
  // `shown` is what the flat says; `current` is what THIS agent may take back. They differ
  // only when a colleague answered first, and that is the case the whole split exists for.
  const shown = current || theirs
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg border px-1.5 py-0.5 ${shown ? 'border-border' : 'border-dashed border-border/60'}`}>
      <span className="text-xs text-muted-foreground">{spec.ka}</span>
      {spec.open
        // An open axis has too many values to pick blind — the chip hands over to the builder
        // with the axis already named, so it is a door, not an answer.
        ? <button type="button" disabled={busy} title={`${spec.ka} — სიის გახსნა`}
            className={pill("add", { door: true, sm: true })}
            onClick={onOpen}>აირჩიეთ ›</button>
        : (spec.answers || ['exclude']).map(p => {
            const a = ANSWERS[p]
            const on = answerKeyOf(shown) === p
            // Lit, but not mine: the answer is drawn and the tap does NOT withdraw it. It
            // records nothing either — re-stating a colleague's answer would fake the second
            // independent observation the certainty layer reads as corroboration. So the
            // button says whose it is and stops, and disagreeing means picking the other
            // polarity, which is a real second opinion.
            const mine = on && answerKeyOf(current) === p
            const theirAnswer = on && !mine
            return (
              // ⚠️ NOT `disabled`, and that is the whole point (2026-08-07). The first cut of
              // this used `disabled` with the explanation in `title` — but a disabled button
              // fires no mouse events in Chrome or Safari, so the tooltip never appears, and
              // it leaves the tab order, so the one element carrying the aria-label became
              // unreachable by exactly the users who most needed it. A lit chip that ignores
              // every attempt to interact with it and never says why is a broken control, not
              // a protected one. `aria-disabled` keeps it focusable and announced; the click
              // is answered with a sentence instead of a withdrawal.
              <button key={p} type="button" disabled={busy}
                aria-disabled={theirAnswer || undefined}
                title={`${spec.ka} — ${a.ka}${mine ? ' (დაჭერით გაუქმდება)'
                  : theirAnswer ? ' — სხვა აგენტის ჩანაწერი' : ''}`}
                aria-label={`${spec.ka} — ${a.ka}${theirAnswer ? ' — სხვა აგენტის ჩანაწერი' : ''}`}
                // TONE STAYS, `door` GOES. The design system already draws this distinction:
                // PILL_DOOR's own comment is "a pill that is a FACT must not [get the hover],
                // or a colleague's read-only value looks like a door". So the answer keeps its
                // full-contrast colour — it is true about the flat and must stay legible —
                // and loses the hover and the pointer cursor. The earlier `opacity-70` said
                // the same thing by dimming, which dims the CONTRAST of the one state carrying
                // new meaning, in a palette nobody had measured for it.
                // ⚠️ `cursor-default` IS NOT REDUNDANT, measured in the browser 2026-08-07.
                // Dropping `door` removes the hover but NOT the pointer: Tailwind's preflight
                // carries `button, [role="button"] { cursor: pointer }`, so every pill rendered
                // as a <button> looks clickable no matter what `pill()` was asked for — which
                // means PILL_DOOR's own `cursor-pointer` is a no-op on buttons and its absence
                // signals nothing. Any other button-shaped pill relying on `door:false` to look
                // inert has the same hole.
                className={pill(on ? a.tone : 'neutral',
                  { door: !theirAnswer, sm: true, extra: theirAnswer ? 'cursor-default' : '' })}
                onClick={() => mine ? onWithdraw(current)
                  : theirAnswer ? onBlocked?.(shown)
                  : onPick(p)}>
                <Icon name={a.icon} />
              </button>
            )
          })}
    </span>
  )
}

// The byline. It answers the question the repeated rows made unanswerable: WHO said this, and
// is this one statement or several people agreeing? It SUPERSEDES the category label added on
// 2026-08-07 ("სხვა აგენტის ჩანაწერი") — that label is why the reported card could be read at
// all, and also why eight rows of it read as eight colleagues.
//
// ⚠️ The count is of RECORDERS, not rows. After 0493 one recorder cannot hold the same live
// rule twice, so the two are the same number going forward — but the rows written before it
// are still here, and a card saying "3" over one agent's three echoes would re-tell exactly
// the lie this whole change exists to end.
function RecorderLine({ members, agentId }) {
  const names = recordersOf(members, agentId)
  if (!names.length) return null
  const readOnly = members.every(m => !ruleEditable(m, agentId)
    && m.source_kind !== 'agent' && m.source_kind !== 'admin')
  return (
    <span className="block text-[10px] tracking-wide text-muted-foreground/70">
      {names.length > 1 ? <Icon name="check" /> : null}
      {names.length > 1 ? ' ჩაწერეს: ' : ' ჩაწერა: '}
      {names.map((n, i) => (
        <span key={n.label} className={n.mine ? 'text-foreground/80' : undefined}>
          {i ? ', ' : ''}{n.label}
        </span>
      ))}
      {/* The whole point of leaving corroboration un-deduped: independent agreement is what
          evidence-certainty reads to call a fact verified, and it should be visible as such
          rather than looking like the duplication bug sitting next to it. */}
      {names.length > 1 && <span className="opacity-70"> · {names.length} დამოუკიდებელი ჩანაწერი</span>}
      {readOnly && <span className="opacity-70"> · მხოლოდ სანახავად</span>}
    </span>
  )
}

function RuleRow({ rule, group, groups, vocab, rules, expansion, busy, agentId, canExcept: excAllowed, onRetract, onExcept }) {
  const s = ruleSentence(rule, vocab, { hideAxes: axesSpokenBy(rules.filter(o => o !== rule)) })
  const legacy = rule.origin === 'legacy_stance'
  const members = group?.members || [rule]
  // Exceptions group by the same key as their parents do — an exception two agents recorded is
  // one carve with two witnesses, and drawing it twice under one rule would reintroduce the
  // repetition one level down, where it reads even more like a contradiction.
  const exceptions = (groups || rules.map(r => ({ key: idOf(r), rep: r, members: [r] })))
    .filter(g => isExceptionOf(rule, g.rep, expansion))
  const canDelete = members.some(m => ruleEditable(m, agentId))
  return (
    <div className="rounded-lg border border-border px-2.5 py-1.5 text-sm">
      <div className="flex items-start gap-2">
        <span className="flex-1 min-w-0">
          {/* ONE line, not two (owner-ruled 2026-08-04). `structured` is a FALLBACK, not a
              companion: it exists so a value with no stored case form (0402) never renders as
              ungrammatical prose. When the sentence is complete the fallback is duplication —
              the same fact twice, the second time as a verbless fragment that reads like a
              different statement. A safety net should not be visible when the acrobat is on
              the platform. `— ერთდროულად` still rides on `structured` so that when the
              fallback DOES fire on a multi-axis rule, the AND is explicit and cannot be
              misread as a list of alternatives. */}
          <span className={s.complete ? undefined : 'text-foreground'}>
            {s.complete ? s.headline : s.structured}
          </span>
          {rule.verbatim && <span className="block text-xs italic text-muted-foreground/80">„{rule.verbatim}“</span>}
          {exceptions.map(eg => {
            const e = eg.rep
            const es = ruleSentence(e, vocab)
            const eNames = recordersOf(eg.members, agentId)
            return (
              <span key={eg.key} className="flex items-start gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                {/* "ვიწრო სძლევს ფართოს" described the RULES ENGINE, on every row. The agent
                    needs the reality of the property, not the precedence algorithm. */}
                <span className="flex-1 min-w-0">
                  ↳ გამონაკლისი: {es.complete ? es.headline : es.structured}
                  {eNames.length > 1 && (
                    <span className="opacity-70"> · {eNames.map(n => n.label).join(', ')}</span>
                  )}
                </span>
                {/* The chain link is the exception's ONLY appearance, so it carries its own
                    delete — without this, nesting the card away makes it unremovable. */}
                {eg.members.some(m => ruleEditable(m, agentId)) && (
                  <button type="button" disabled={busy} title="გამონაკლისის წაშლა"
                    className="flex-none text-muted-foreground hover:text-destructive"
                    onClick={() => onRetract(eg)}>✕</button>
                )}
              </span>
            )
          })}
          {s.untouched.length > 0 && (
            <span className="block text-xs text-muted-foreground/60">
              {s.untouched.join(' · ')} — არ არის შეზღუდული
            </span>
          )}
        </span>
        <span className="flex-none flex items-center gap-1">
          {/* Offered only where an exception is a real sentence — see canExcept/EXC_OFFER. */}
          {excAllowed && (
            <button type="button" disabled={busy} title="გამონაკლისი"
              className={pill("add", { door: true, sm: true })}
              onClick={onExcept}>გამონაკლისი</button>
          )}
          {/* An AGENT-recorded legacy stance withdraws from here now (upgrade-on-touch:
              the delete goes through the stance's own clear path). A scrape/ai stance
              shows no delete — it is the portal's or the extractor's observation, not any
              agent's to withdraw, and a dead button would be a lie about what this surface
              controls. */}
          {/* Was `!legacy || legacyEditable(...)` — i.e. every RULE drew a live 🗑 no matter
              whose it was, which is the button irakli pressed on Gvantsa's rule. */}
          {/* ⚠️ Now keyed on whether ANY member is mine, not on the representative. A grouped
              card can be led by a colleague's earlier statement while still holding my own,
              and a 🗑 that disappeared because someone else got there first would strand my
              observation with no way to withdraw it. */}
          {canDelete && (
            <button type="button" disabled={busy} title={members.length > 1 ? 'ჩემი ჩანაწერის წაშლა' : 'წაშლა'}
              className="text-muted-foreground hover:text-destructive" onClick={() => onRetract(null)}>
              <Icon name="trash" />
            </button>
          )}
        </span>
      </div>
      {/* THE LABEL IS NOW A NAME. 2026-08-07 put the category on rules as well as stances,
          which is what stopped irakli reading Gvantsa's rule as his own draft — but a CATEGORY
          cannot distinguish four colleagues agreeing from one save loop firing four times, and
          the next day the owner met a card doing exactly that. RecorderLine keeps the same
          three honest answers (mine / a person / a channel) and names the person.
          `ადრე ჩაწერილი` stays as its own line: it says WHEN, not who, and only the upgrade-
          on-touch conversion depends on the agent seeing it. */}
      <RecorderLine members={members} agentId={agentId} />
      {legacy && ruleEditable(rule, agentId) && (
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60">ადრე ჩაწერილი</span>
      )}
    </div>
  )
}

function Builder({ draft, setDraft, axes, valuesFor, vocab, expansion, rules, busy,
                   expandedAxes, onExpandAxis, onCancel, onSave }) {
  const parent = draft.parent || null
  const live = livePredicateOf(draft.predicate)
  const named = Object.keys(live)
  const ok = named.length > 0
  // Same withholding as the recorded card: the sentence being approved must not promise the
  // agent that an axis is open when another rule on this listing has already closed it.
  const preview = ok ? ruleSentence(draft, vocab, { hideAxes: axesSpokenBy(rules) }) : null
  // For an exception the pair must actually subsume, or the agent is building a rule that
  // will sit beside its "parent" and do nothing. COMPUTED by the same subsumes() the scorer's
  // predicateSubsumes is parity-tested against — never asserted, because asserting it would
  // promise a relationship the matcher may not honour.
  const carves = parent && ok ? subsumes(parent, draft, expansion) : true
  // A rule already on file that covers this draft. See the copy below for why an exclusion
  // and a permission get opposite messages.
  const broader = !parent && ok ? rules.find(r => subsumes(r, draft, expansion)) : null

  const set = (axis, values) => setDraft(d => {
    const pred = { ...d.predicate, [axis]: values }
    // An exception keeps its parent's axes visible even when emptied — that IS the shape it
    // is refining, and record() strips them before anything keys on the predicate. A fresh
    // rule drops them on the spot.
    if (!values.length && !parent) delete pred[axis]
    return { ...d, predicate: pred }
  })
  const toggle = (axis, value) => {
    const cur = draft.predicate[axis] || []
    // picking a real value cancels ნებისმიერი — "any nationality, and also Georgian" is not
    // a sentence, and the two answers key on different objects (clearance vs rule).
    setDraft(d => ({ ...d, anyAxes: (d.anyAxes || []).filter(a => a !== axis) }))
    set(axis, cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value])
  }
  const anyAxes = new Set(draft.anyAxes || [])
  const toggleAny = (axis) => setDraft(d => {
    const cur = new Set(d.anyAxes || [])
    const pred = { ...d.predicate }
    if (cur.has(axis)) cur.delete(axis)
    else { cur.add(axis); delete pred[axis] }   // exclusive with this axis's values
    return { ...d, anyAxes: [...cur], predicate: pred }
  })

  return (
    <div className="rounded-lg border border-primary/40 p-2.5 space-y-2">
      {parent && (
        <p className="text-xs text-muted-foreground">
          გამონაკლისი: <span className="text-foreground">{sentenceOf(parent, vocab)}</span>
        </p>
      )}
      {/* Polarity FIRST and full width — owners lead with it ("no X" / "only X"), and this
          is direction A's four-up kept as B's polarity menu (the graft the verdict made).
          An exception's polarity is not offered: being the carve is what makes it one, and
          which carve it is follows from the parent (B316). */}
      {!parent && (
        <div className="flex flex-wrap gap-1.5">
          {POLARITIES.map(p => (
            <button key={p.v} type="button"
              className={pill(draft.polarity === p.v ? p.tone : 'neutral', { door: true, sm: true })}
              onClick={() => setDraft(d => ({ ...d, polarity: p.v }))}>
              <Icon name={p.icon} /> {p.ka}
            </button>
          ))}
        </div>
      )}

      {/* 0927 — "no pets, BUT ask me", DIRECTLY UNDER THE POLARITY IT QUALIFIES (owner,
          2026-09-06: "add selectors negotiable under not allowed and exception"). It sat beside
          the verbatim field for one commit, half a panel away from the ✗ it modifies, where it
          read as a property of the note rather than of the answer.
          ⚠️ REFUSALS ONLY, and that is why an exception under "no pets" does not offer it: that
          exception's polarity is `allow`, and "დაიშვება პატარა ძაღლი — თუმცა მოლაპარაკება
          შესაძლებელია" reads as though the permission itself were in doubt, which is the
          opposite of what the owner said. An exception under an "only X" rule IS a refusal, and
          there the box appears — which is the exception case that can carry it honestly.
          Scoped to pets by instruction; the column behind it is general, so widening to children
          or smoking later is this condition and nothing else.
          NATIONALITY added 2026-09-16 (owner: "add nationality negotiable as we have on pet") —
          "450$ for foreigners", "this price is for Georgians": not a refusal, other terms. */}
      {(draft.polarity === 'exclude' || draft.polarity === 'accept_only')
        && ('pet_species' in (draft.predicate || {}) || 'pet_size' in (draft.predicate || {})
            || 'nationality' in (draft.predicate || {})) && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-negotiable>
          <input type="checkbox" checked={draft.negotiable === true}
            onChange={e => setDraft(d => ({ ...d, negotiable: e.target.checked }))} />
          <Icon name="handshake" />
          <span>მოლაპარაკება შესაძლებელია</span>
          {/* Says what it DOES, because the effect is invisible from this panel: the flat stops
              being hidden from clients with a pet and is shown with "ask the owner" instead. */}
          <span className="text-[11px] opacity-70">— კლიენტს მაინც ვაჩვენებთ, შეკითხვის რჩევით</span>
        </label>
      )}

      {axes.map(a => {
        let vals = valuesFor(a.code)
        if (!vals.length) return null              // range axes have no value list yet
        // ⚠️ ON AN AXIS THE PARENT NAMES, ONLY THE PARENT'S OWN SET IS OFFERED — plus whatever
        // the expansion says lives INSIDE it at tier `definite`. The full vocabulary here was
        // the "student პენსიონერი" bug (owner, 2026-08-04): under "no students" the profession
        // axis listed all eight professions, inviting "except a pensioner" — a non-sequitur
        // about people who were never excluded, and a pick subsumes() then rejects with a
        // message the agent has no way to act on. What subdivides "no students" is the OTHER
        // axes; this one can only be kept or narrowed. (Under "no foreigners" the same rule
        // OFFERS Indian, Pakistani… — the definite children — which is exactly the descent
        // the widened subsumption accepts.)
        const parentVals = parent && Array.isArray(livePredicateOf(parent.predicate)[a.code])
          ? livePredicateOf(parent.predicate)[a.code] : null
        if (parentVals) {
          const exp = expansion[a.code] || {}
          const allowed = new Set(parentVals.map(String))
          for (const pv of parentVals)
            for (const [child, tier] of Object.entries(exp[pv] || {}))
              if (tier === 'definite') allowed.add(String(child))
          vals = vals.filter(v => allowed.has(String(v.value)))
        }
        const picked = draft.predicate[a.code] || []

        // ── რაოდენობა IS A THRESHOLD PICKER, NOT NUMERAL CHIPS (owner, 2026-08-04) ──
        // Independent numerals let an agent record {2}, and "არ იღებს 2 სტუდენტს" read
        // literally accepts 1, 3 and 5 students while refusing exactly two — the matcher
        // agrees with that absurd reading, so the UI must not be able to compose it. An owner
        // states a BOUND: permissive rules offer caps ("მაქს. 2" → {1,2}), exclusions offer
        // excesses ("2-ზე მეტი" → {3,4,5,6+}). One choice at a time; tapping the active one
        // clears the axis. The predicate stays an enum SET, so subsumption needs nothing new.
        if (a.code === 'group_size') {
          const permissive = draft.polarity === 'allow' || draft.polarity === 'prefer'
          const nums = vals.map(v => v.value).filter(v => v !== '6+')
          const top = vals.some(v => v.value === '6+') ? ['6+'] : []
          // An exception under a parent that itself names a count carves out of the PARENT'S
          // set, not out of 1..k. "არ იღებს 2-ზე მეტს" ({3,4,5,6+}) with a family exception
          // capped at 4 must store {3,4} — the members of the excluded set the owner is
          // letting back in — or subsumption correctly refuses the pair.
          const parentSet = Array.isArray(parentVals) ? parentVals : null
          return (
            <div key={a.code}>
              <p className="text-[11px] text-muted-foreground">{a.name_ka || a.code}</p>
              <div className="flex flex-wrap gap-1">
                {nums.map((k, i) => {
                  let bound = permissive ? nums.slice(0, i + 1) : nums.slice(i + 1).concat(top)
                  if (parentSet) bound = bound.filter(x => parentSet.includes(x))
                  if (!bound.length) return null
                  const on = picked.length === bound.length && bound.every(x => picked.includes(x))
                  return (
                    <button key={k} type="button" disabled={busy}
                      className={pill(on ? 'info' : 'neutral', { door: true, sm: true })}
                      onClick={() => set(a.code, on ? [] : bound)}>
                      {permissive ? `მაქს. ${k}` : `${k}-ზე მეტი`}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        }

        // ⚠️ AN AXIS IS EXPANDED ONLY WHEN IT IS IN PLAY. The condition here included
        // `named.length === 0`, i.e. expand EVERYTHING while nothing is chosen — exactly
        // backwards, and it opened the builder as a wall of ~47 values with nothing selected
        // ("nothing is selected but why is it open?"). In play means: this axis has values
        // picked, or the draft was opened against it (the ეროვნება door), or the agent asked.
        const showAll = picked.length > 0 || (a.code in draft.predicate) || expandedAxes.has(a.code)
        const shown = showAll ? vals : vals.slice(0, 6)
        return (
          <div key={a.code}>
            <p className="text-[11px] text-muted-foreground">{a.name_ka || a.code}</p>
            <div className="flex flex-wrap gap-1">
              {/* ── ნებისმიერი — A MODE, NOT A VALUE (owner, 2026-08-26) ──────────────────
                  "any nationality is accepted" is a real and useful fact — an agent can send
                  the flat to anyone and know it. It is NOT a member of the vocabulary: writing
                  {nationality:["any"]} would claim "any" is a nationality, and
                  assert_restriction_rule refuses an empty array outright, so a rule cannot
                  express it at all. It is a per-axis CLEARANCE — core 0739's
                  listing_restriction_clear.axes — which is a different object from a rule.
                  ⚠️ ONLY UNDER დაიშვება (owner). "არ იღებს ნებისმიერს" / "მხოლოდ ნებისმიერი" /
                  "სასურველია ნებისმიერი" are not statements anyone makes; offering the chip
                  under them would invite a recorded nonsense the matcher would then have to
                  interpret.
                  ⚠️ And it is EXCLUSIVE with the value chips: picking it clears the axis's
                  values and picking a value clears it. "any nationality, and also Georgian"
                  is not a sentence. */}
              {draft.polarity === 'allow' && (
                <button type="button" disabled={busy}
                  title="აქ შეზღუდვა არ არის — ნებისმიერი მიიღება"
                  className={pill(anyAxes.has(a.code) ? 'ok' : 'neutral', { door: true, sm: true })}
                  onClick={() => toggleAny(a.code)}>
                  ნებისმიერი
                </button>
              )}
              {shown.map(v => (
                <button key={v.value} type="button" disabled={busy || anyAxes.has(a.code)}
                  className={pill(picked.includes(v.value) ? 'info' : 'neutral', { door: true, sm: true, extra: anyAxes.has(a.code) ? 'opacity-40' : '' })}
                  onClick={() => toggle(a.code, v.value)}>
                  {v.name_ka || v.value}
                </button>
              ))}
              {/* The rest are one click away and SAY HOW MANY — a silent cut reads as "that is
                  the whole vocabulary", which on ეროვნება would hide 15 of its 21 values. */}
              {!showAll && vals.length > shown.length && (
                <button type="button" disabled={busy} title={`${a.name_ka || a.code} — სრული სია`}
                  className={pill("add", { door: true, sm: true })}
                  onClick={() => onExpandAxis(a.code)}>+{vals.length - shown.length}</button>
              )}
            </div>
          </div>
        )
      })}

      {/* THE HONESTY LINE. Generated, never written per case.
          ⚠ The complete-check is the same one the recorded row makes, and matters MORE here:
          this is the sentence an agent approves before recording, so it is the one place
          ungrammatical Georgian must never appear. This printed `preview.headline`
          unconditionally until 2026-08-04 (core B308). */}
      <div className="rounded-md bg-muted/40 px-2 py-1.5">
        <p className="text-sm">{preview ? (preview.complete ? preview.headline : preview.structured) : 'ვინ?'}</p>
        {preview && preview.untouched.length > 0 && (
          <p className="text-xs text-muted-foreground/60">
            {preview.untouched.join(' · ')} — არ არის შეზღუდული
          </p>
        )}
        {parent && ok && (
          <p className={`text-xs ${carves ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {carves
              ? 'გამონაკლისი ამ წესიდან — ორივე ჩაიწერება'
              : 'ეს არ არის გამონაკლისი ამ წესიდან — ცალკე პირობად ჩაიწერება'}
          </p>
        )}
        {/* ⚠ A MODIFIER ON AN EXCLUSION NARROWS IT — it does not tighten it. Owner,
            2026-08-04: "არ დაიშვება ძაღლი includes little dog too." An agent who has already
            recorded "no dogs" and then adds "no small dogs" has recorded nothing new, and one
            who thinks they are being stricter has it exactly backwards: naming a size is what
            would let big dogs in, if the broad rule were not there. Redundant, not wrong — so
            this says so rather than blocking. */}
        {broader && (
          <p className={`text-xs ${(draft.polarity === 'allow' || draft.polarity === 'prefer')
            ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {(draft.polarity === 'allow' || draft.polarity === 'prefer')
              ? `ავიწროებს: ${sentenceOf(broader, vocab)}`
              : `უკვე დაფარულია — „${sentenceOf(broader, vocab)}“ ამასაც მოიცავს`}
          </p>
        )}
      </div>

      {/* Verbatim LAST and never required: tenant_pref.notes is filled on 0 of 651 rows.
          Design for it being empty; a prominent field agents ignore is worse than a small one. */}
      {/* Verbatim LAST and never required: tenant_pref.notes is filled on 0 of 651 rows.
          Design for it being empty; a prominent field agents ignore is worse than a small one. */}
      <input className={inputCompact} placeholder="რა თქვა მესაკუთრემ (არასავალდებულო)"
        value={draft.verbatim || ''} onChange={e => setDraft(d => ({ ...d, verbatim: e.target.value }))} />

      <div className="flex gap-2">
        {/* review 2026-09-23: ნებისმიერი alone (no named value) is saveable too — record() sends it
            as an axis clearance. Not for an exception, which must carve out of its parent. */}
        <button type="button" className={btn('primary')} disabled={!(ok || (!parent && anyAxes.size > 0)) || busy} onClick={onSave}>
          {parent ? 'ორივეს დაფიქსირება' : 'დაფიქსირება'}
        </button>
        <button type="button" className={btn('ghost')} disabled={busy} onClick={onCancel}>გაუქმება</button>
      </div>
    </div>
  )
}

// The one place a rule is turned into a label outside a row — same complete-check, because a
// missing case form (0402) reads as ungrammatical prose wherever it lands.
function sentenceOf(rule, vocab) {
  const s = ruleSentence(rule, vocab)
  return s.complete ? s.headline : s.structured
}
